import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE, api } from "../../api/client";

export interface VersionInfo {
  id: string;
  seq: number;
  created_at: string;
  message: string;
  user: string | null;
  auto: boolean;
  parent: string | null;
  stats: { added?: number; removed?: number; modified?: number };
  tags: string[];
  file_count: number;
  size: number;
  tree: string;
  files?: Record<string, { sha: string; size: number }>;
  created?: boolean;
}

export interface VersionsList {
  versions: VersionInfo[];
  latest: string | null;
  dirty: { changes: number; added: number; removed: number; modified: number; paths: string[] } | null;
}

export interface FileChange {
  path: string;
  status: "added" | "removed" | "modified";
  old_size: number | null;
  new_size: number | null;
  binary: boolean;
  diff: string;
  truncated: boolean;
}

export interface VersionDiff {
  from: string;
  to: string;
  changes: FileChange[];
  summary: { added: number; removed: number; modified: number };
}

export const versionKeys = {
  list: (p: string) => ["versions", p] as const,
  detail: (p: string, v: string) => ["version", p, v] as const,
  diff: (p: string, v: string, against: string) => ["version-diff", p, v, against] as const,
};

export const useVersions = (projectId: string | null) =>
  useQuery({
    queryKey: versionKeys.list(projectId ?? ""),
    queryFn: () => api.get<VersionsList>(`/projects/${projectId}/versions`),
    enabled: !!projectId,
    refetchOnWindowFocus: true,
  });

export const useVersion = (projectId: string | null, versionId: string | null) =>
  useQuery({
    queryKey: versionKeys.detail(projectId ?? "", versionId ?? ""),
    queryFn: () => api.get<VersionInfo>(`/projects/${projectId}/versions/${versionId}`),
    enabled: !!projectId && !!versionId,
  });

export const useVersionDiff = (projectId: string | null, versionId: string | null, against: string) =>
  useQuery({
    queryKey: versionKeys.diff(projectId ?? "", versionId ?? "", against),
    queryFn: () =>
      api.get<VersionDiff>(`/projects/${projectId}/versions/${versionId}/diff${against && against !== "working" ? `?against=${against}` : ""}`),
    enabled: !!projectId && !!versionId,
  });

export function versionFileUrl(projectId: string, versionId: string, path: string): string {
  return `${API_BASE}/projects/${projectId}/versions/${versionId}/files/${path}`;
}

export function exportUrl(projectId: string, versionId?: string): string {
  return `${API_BASE}/projects/${projectId}/export${versionId ? `?version=${versionId}` : ""}`;
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>, projectId: string | null) {
  qc.invalidateQueries({ queryKey: versionKeys.list(projectId ?? "") });
  qc.invalidateQueries({ queryKey: ["version-diff", projectId] });
}

export function useCreateVersion(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { message: string; tags?: string[]; force?: boolean; auto?: boolean }) =>
      api.post<VersionInfo>(`/projects/${projectId}/versions`, body),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function useRestoreVersion(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, paths }: { versionId: string; paths?: string[] }) =>
      api.post<{ version: string; checkpoint: string | null; written: string[]; removed: string[] }>(
        `/projects/${projectId}/versions/${versionId}/restore`,
        { paths },
      ),
    onSuccess: () => {
      invalidateAll(qc, projectId);
      qc.invalidateQueries({ queryKey: ["files", projectId] });
      qc.invalidateQueries({ queryKey: ["file", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["catalog", projectId] });
      qc.invalidateQueries({ queryKey: ["reports", projectId] });
      qc.invalidateQueries({ queryKey: ["inputs", projectId] });
      qc.invalidateQueries({ queryKey: ["derived", projectId] });
      qc.invalidateQueries({ queryKey: ["tests", projectId] });
    },
  });
}

export function useDeleteVersion(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => api.del<void>(`/projects/${projectId}/versions/${versionId}`),
    onSuccess: () => invalidateAll(qc, projectId),
  });
}

export function useImportProjectPath() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { path: string; name?: string }) => api.post<{ id: string; name: string }>(`/projects/import-path`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

export function useImportProjectZip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, name }: { file: File; name?: string }) => {
      const fd = new FormData();
      fd.append("archive", file);
      if (name) fd.append("name", name);
      const res = await fetch(`${API_BASE}/projects/import`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      return (await res.json()) as { id: string; name: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}
